import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Header } from "@/components/Header";
import { Hero } from "@/components/Hero";
import { Marquee } from "@/components/Marquee";
import { TrustBar } from "@/components/TrustBar";
import { OffersGrid } from "@/components/OffersGrid";
import { WhyBroker } from "@/components/WhyBroker";
import { HowItWorks } from "@/components/HowItWorks";
import { WhatsIncluded } from "@/components/WhatsIncluded";
import { ConsultantCard } from "@/components/ConsultantCard";
import { PreventivoSection } from "@/components/PreventivoSection";
import { Faq } from "@/components/Faq";
import { CtaFinal } from "@/components/CtaFinal";
import { MobileStickyBar } from "@/components/MobileStickyBar";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { useSeo, faqSchema } from "@/lib/seo";
import { FAQ_PIVA, RACING_IMAGES, waLink } from "@/data/content";

const HERO_PHOTO = "https://images.unsplash.com/photo-1732347700505-e1231e0f7c94?crop=entropy&cs=srgb&fm=jpg&q=85";

const FISCALI = [
  { profilo: "Professionisti e ditte individuali", deduzione: "20%", limite: "max € 3.615,20 / anno", iva: "40%" },
  { profilo: "Agenti e rappresentanti di commercio", deduzione: "80%", limite: "max € 5.164,57 / anno", iva: "100%" },
  { profilo: "Auto in uso promiscuo ai dipendenti", deduzione: "70%", limite: "nessun limite di importo", iva: "40% · 100% se strumentale" },
];

const TaxBenefits = () => (
  <section className="mx-auto max-w-7xl px-5 py-16 lg:px-8 lg:py-20" data-testid="tax-benefits">
    <Reveal>
      <p className="text-xs font-semibold uppercase tracking-widest text-xmove-orange">Vantaggi fiscali</p>
      <h2 className="mt-3 max-w-2xl font-display text-3xl font-bold text-xmove-ink sm:text-4xl">
        Deducibilità e detraibilità in numeri
      </h2>
      <p className="mt-3 max-w-2xl text-base text-xmove-muted md:text-lg">
        Art. 164 TUIR: quanto scarica davvero la tua partita IVA dal canone di noleggio.
      </p>
    </Reveal>
    <Reveal delay={0.1}>
      <div className="mt-10 overflow-hidden rounded-2xl border border-xmove-ink/8 bg-white shadow-soft">
        <div className="hidden grid-cols-4 gap-4 border-b border-xmove-ink/8 bg-xmove-cream px-6 py-4 text-xs font-bold uppercase tracking-wider text-xmove-muted md:grid">
          <span>Profilo</span><span>Costo deducibile</span><span>Limite annuo</span><span>IVA detraibile</span>
        </div>
        {FISCALI.map((r) => (
          <div key={r.profilo} className="grid grid-cols-2 gap-x-4 gap-y-2 border-b border-xmove-ink/6 px-6 py-5 last:border-0 md:grid-cols-4 md:items-center">
            <span className="col-span-2 text-sm font-bold text-xmove-ink md:col-span-1">{r.profilo}</span>
            <span className="text-sm text-xmove-ink"><span className="text-xs text-xmove-muted md:hidden">Deduzione: </span><b className="text-xmove-orange">{r.deduzione}</b></span>
            <span className="text-sm text-xmove-muted">{r.limite}</span>
            <span className="col-span-2 text-sm md:col-span-1"><span className="text-xs text-xmove-muted md:hidden">IVA: </span><b className="text-xmove-green">{r.iva}</b></span>
          </div>
        ))}
      </div>
      <p className="mt-3 text-xs text-xmove-muted" data-testid="tax-disclaimer">*Tabella puramente indicativa: consultare sempre il proprio commercialista.</p>
    </Reveal>
    <Reveal delay={0.15}>
      <Link
        to="/guide/deducibilita-noleggio-auto-partita-iva/"
        data-testid="piva-guide-link"
        className="mt-6 inline-flex items-center gap-2 font-display text-sm font-bold text-xmove-orange transition-colors hover:text-xmove-orangeDark"
      >
        Leggi la guida completa alla deducibilità 2026 <ArrowRight size={16} />
      </Link>
    </Reveal>
  </section>
);

const PivaSeoContent = () => (
  <section className="mx-auto max-w-3xl px-5 py-16 lg:px-8" data-testid="piva-seo-content">
    <Reveal>
      <h2 className="font-display text-2xl font-bold text-xmove-ink sm:text-3xl">
        Noleggio lungo termine per partite IVA: il costo dell'auto diventa prevedibile
      </h2>
      <div className="mt-6 space-y-5 text-base leading-relaxed text-xmove-muted">
        <p>
          Se hai una partita IVA, l'auto è uno strumento di lavoro: deve esserci sempre, non deve mai
          fermarsi e non deve riservare sorprese in contabilità. Il noleggio a lungo termine trasforma
          un bene che si svaluta in un costo fisso documentato mese per mese, con una fattura unica che
          il tuo commercialista registra in un minuto.
        </p>
        <h3 className="font-display text-xl font-bold text-xmove-ink">Una fattura, tutti i servizi</h3>
        <p>
          Assicurazione RCA e Kasko, manutenzione ordinaria e straordinaria, soccorso stradale,
          protezione cristalli, tutela legale, infortunio conducente ed eventi atmosferici sono dentro il canone. Niente polizze separate, niente fatture di officina sparse,
          niente scadenze da ricordare: a fine mese hai un solo documento, parzialmente deducibile e con
          IVA detraibile secondo il tuo profilo.
        </p>
        <h3 className="font-display text-xl font-bold text-xmove-ink">Liquidità libera per il tuo lavoro</h3>
        <p>
          Acquistare o finanziare un'auto lega capitale che potresti investire nella tua attività. Con il
          noleggio l'anticipo è flessibile — spesso riducibile a zero — e il bene non appesantisce il
          bilancio né l'affidamento bancario. Per molte SRL è il motivo decisivo.
        </p>
        <h3 className="font-display text-xl font-bold text-xmove-ink">XMOVE lavora per te, punto</h3>
        <p>
          I listini del noleggio cambiano di continuo e il prezzo giusto dipende dal momento, dal modello
          e dal tuo profilo. XMOVE consulta per te lo stock più ampio d'Italia e seleziona l'offerta
          migliore disponibile in quel momento, con la simulazione fiscale già pronta per il tuo
          commercialista.
        </p>
      </div>
    </Reveal>
  </section>
);

export default function PartitaIva() {
  useSeo({
    title: "Noleggio Lungo Termine Partita IVA: Offerte {Mese Anno} | XMOVE",
    description:
      "Canone fisso tutto incluso, costi deducibili e IVA detraibile. Selezioniamo l'offerta migliore dallo stock più ampio d'Italia. Preventivo gratuito.",
    path: "/noleggio-lungo-termine-partita-iva/",
    jsonLd: [faqSchema(FAQ_PIVA)],
  });

  return (
    <div className="pb-[72px] md:pb-0" data-testid="piva-page">
      <Header />
      <main>
        <Hero
          eyebrow="Dedicato a Partite IVA e Aziende"
          h1Lines={["Noleggio Lungo Termine", "per Partite IVA"]}
          subtitle="Canone fisso tutto incluso, costi deducibili e IVA detraibile. Oltre 1.200 professionisti e aziende si affidano a noi: selezioniamo l'offerta migliore dallo stock più ampio d'Italia."
          photo={HERO_PHOTO}
          photoAlt="Auto business a noleggio lungo termine per partite IVA"
        />
        <Marquee />
        <TrustBar />
        <TaxBenefits />
        <div className="bg-white">
          <OffersGrid
            target="partita-iva"
            title="Offerte selezionate per partite IVA"
            subtitle="Modelli business con canoni negoziati e simulazione fiscale inclusa nel preventivo."
          />
        </div>
        <WhyBroker />
        <HowItWorks />
        <WhatsIncluded dark />
        <PivaSeoContent />
        <ConsultantCard racingBg={RACING_IMAGES[1]} />
        <PreventivoSection
          idPrefix="piva"
          title="Preventivo per la tua partita IVA"
          subtitle="Canone, anticipo e simulazione di deducibilità sul tuo profilo: ti richiamiamo in pochi minuti, gratis e senza impegno."
        />
        <Faq faqs={FAQ_PIVA} title="Domande frequenti — Partita IVA" />
        <CtaFinal
          title="La tua prossima auto aziendale, senza immobilizzare capitale"
          subtitle="Compila il modulo: ti richiamiamo in pochi minuti con la selezione delle offerte e la simulazione di deducibilità sul tuo profilo."
        />
      </main>
      <Footer />
      <MobileStickyBar waText={waLink("Salve XMOVE, sono una partita IVA e vorrei un preventivo per il noleggio lungo termine.")} />
    </div>
  );
}
